import { useState, useEffect, useContext, lazy, Suspense } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, Play, FileText, Download, X, MessageCircle, User, Phone, Mail, Loader2, MapPin } from "lucide-react";
import { AppContext, API } from "@/App";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ShareButtons from "@/components/ShareButtons";
import RelatedArticles from "@/components/RelatedArticles";
import SimilarProperties from "@/components/SimilarProperties";
import TourRequestButton from "@/components/TourRequestButton";
import PriceAlertButton from "@/components/PriceAlertButton";
import PropertyViewCounter from "@/components/PropertyViewCounter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import SEO, { generatePropertySchema } from "@/components/SEO";
import { useAnalytics } from "@/components/AnalyticsProvider";

// Lazy load map for better performance
const PropertyMap = lazy(() => import("@/components/PropertyMap"));

const WHATSAPP_NUMBER = "50242547204";

const PropertyPage = () => {
  const { slug } = useParams();
  const { formatPrice, formatArea } = useContext(AppContext);
  const { trackConversion } = useAnalytics();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", phone: "", email: "" });

  useEffect(() => {
    // Try to fetch by slug first, fallback to id
    axios.get(`${API}/properties/by-slug/${slug}`)
      .then(res => setProperty(res.data))
      .catch(() => {
        // Fallback to id-based fetch for backwards compatibility
        axios.get(`${API}/properties/${slug}`)
          .then(res => setProperty(res.data))
          .catch(() => {/* Error handled silently */});
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const nextImage = () => {
    if (property?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % property.images.length);
    }
  };

  const prevImage = () => {
    if (property?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
    }
  };

  const selectImage = (index) => {
    setCurrentImageIndex(index);
  };

  const handleWhatsAppClick = async () => {
    // Track conversion event
    trackConversion('whatsapp_click', {
      property_id: property.id,
      property_name: property.name,
      location: 'property_page'
    });
    
    // Log the WhatsApp click to CRM
    try {
      await axios.post(`${API}/leads/whatsapp-click`, {
        name: "WhatsApp Lead",
        phone: "",
        email: "",
        property_id: property.id,
        property_name: property.name,
        interaction_type: "whatsapp_click"
      });
    } catch (error) {
      // Error logged silently - don't block user action
    }
    
    // Open WhatsApp with detailed pre-filled message
    const priceFormatted = formatPrice(property.price_usd);
    const location = `${property.zone}, ${property.department}`;
    const message = `¡Hola Ikonic Deals! 👋

Me interesa la siguiente propiedad:

🏠 *${property.name}*
📍 ${location}
💰 ${priceFormatted}

Me gustaría recibir más información. ¡Gracias!`;
    
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name.trim() || !contactForm.phone.trim()) {
      toast.error("Por favor ingresa tu nombre y número de celular");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/leads/capture`, {
        name: contactForm.name,
        phone: contactForm.phone,
        email: contactForm.email,
        property_id: property.id,
        property_name: property.name,
        interaction_type: "contact_form"
      });
      
      toast.success("¡Gracias! Te contactaremos pronto.");
      setShowContactModal(false);
      setContactForm({ name: "", phone: "", email: "" });
    } catch (error) {
      toast.error("Hubo un error. Por favor intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <div className="pt-32 pb-24 container-custom text-center">
          <p className="text-white">Cargando...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <div className="pt-32 pb-24 container-custom text-center">
          <h1 className="text-2xl text-white mb-4">Propiedad no encontrada</h1>
          <Link to="/propiedades" className="text-[#D4AF37]">Ver propiedades</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images = property.images || [];
  const videos = property.videos || [];
  const documents = property.documents || [];
  const mainImage = images.length > 0 ? images[currentImageIndex]?.url : "https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg";

  // Generate SEO data for this property
  const propertyTitle = `${property.name} | ${property.zone}, ${property.department} | Ikonic Deals`;
  const propertyDescription = property.description?.substring(0, 155) + '...' || 
    `${property.type} en ${property.transaction_type} en ${property.zone}, ${property.department}. ${property.bedrooms || 0} habitaciones, ${property.bathrooms || 0} baños. Precio: $${property.price_usd?.toLocaleString()}`;
  const propertyImage = images.length > 0 ? images[0]?.url : mainImage;
  const propertySchema = generatePropertySchema(property);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="property-detail-page">
      <SEO 
        title={propertyTitle}
        description={propertyDescription}
        image={propertyImage}
        url={`/propiedad/${property.slug || property.id}`}
        type="product"
        schema={propertySchema}
      />
      <Header />
      <section className="pt-32 pb-24">
        <div className="container-custom">
          <Link to="/propiedades" className="text-[#A3A3A3] hover:text-[#D4AF37] mb-8 inline-block">
            ← Volver
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {/* Main Image with Navigation */}
              <div className="relative aspect-video overflow-hidden rounded-sm group">
                <img 
                  src={mainImage} 
                  alt={property.name} 
                  className="w-full h-full object-cover transition-transform duration-300"
                  data-testid="main-property-image"
                />
                
                {/* Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid="prev-image-btn"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid="next-image-btn"
                    >
                      <ChevronRight size={24} />
                    </button>
                    
                    {/* Image Counter */}
                    <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 rounded-sm text-sm">
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  </>
                )}
              </div>
              
              {/* Thumbnail Gallery */}
              {images.length > 1 && (
                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2 mt-4">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => selectImage(i)}
                      className={`aspect-video overflow-hidden rounded-sm transition-all ${
                        i === currentImageIndex 
                          ? "ring-2 ring-[#D4AF37] opacity-100" 
                          : "opacity-60 hover:opacity-100"
                      }`}
                      data-testid={`thumbnail-${i}`}
                    >
                      <img 
                        src={img.url} 
                        alt={img.alt || `Imagen ${i + 1}`} 
                        className="w-full h-full object-cover" 
                      />
                    </button>
                  ))}
                </div>
              )}
              
              {/* Description with preserved formatting */}
              <div className="mt-8">
                <h2 className="text-2xl text-white mb-4">Descripción</h2>
                <p className="text-[#A3A3A3] whitespace-pre-wrap">{property.description || "Sin descripción"}</p>
              </div>

              {/* Videos Section */}
              {videos.length > 0 && (
                <div className="mt-8">
                  <h2 className="text-2xl text-white mb-4 flex items-center gap-2">
                    <Play size={24} className="text-[#D4AF37]" />
                    Videos
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {videos.map((video, i) => (
                      <div key={i} className="relative aspect-video rounded-sm overflow-hidden bg-black/40">
                        <video controls className="w-full h-full object-cover" data-testid={`video-${i}`}>
                          <source src={video.url} type="video/mp4" />
                          Tu navegador no soporta videos.
                        </video>
                        {video.title && <p className="text-white text-sm mt-2">{video.title}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents Section */}
              {documents.length > 0 && (
                <div className="mt-8">
                  <h2 className="text-2xl text-white mb-4 flex items-center gap-2">
                    <FileText size={24} className="text-[#D4AF37]" />
                    Documentos
                  </h2>
                  <div className="space-y-3">
                    {documents.map((doc, i) => (
                      <a
                        key={i}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-4 bg-[#121212] border border-white/5 rounded-sm hover:border-[#D4AF37]/50 transition-colors"
                        data-testid={`document-${i}`}
                      >
                        <FileText size={20} className="text-[#D4AF37] shrink-0" />
                        <span className="text-white flex-1">{doc.title || doc.filename || `Documento ${i + 1}`}</span>
                        <Download size={18} className="text-[#A3A3A3]" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Location Map */}
              {(property.latitude && property.longitude) && (
                <div className="mt-8">
                  <h2 className="text-2xl text-white mb-4 flex items-center gap-2">
                    <MapPin size={24} className="text-[#D4AF37]" />
                    Ubicación
                  </h2>
                  <Suspense fallback={
                    <div className="h-[400px] bg-[#121212] rounded-lg flex items-center justify-center">
                      <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
                    </div>
                  }>
                    <PropertyMap 
                      properties={[property]}
                      formatPrice={formatPrice}
                      formatArea={formatArea}
                      height="400px"
                    />
                  </Suspense>
                  <p className="text-[#A3A3A3] text-sm mt-3">
                    {property.address}, {property.zone}, {property.department}
                  </p>
                </div>
              )}
            </div>
            
            {/* Property Info Sidebar */}
            <div className="bg-[#121212] border border-white/5 p-6 h-fit sticky top-24">
              <span className="text-[#D4AF37] text-sm uppercase">{property.property_type}</span>
              <h1 className="text-2xl text-white mt-2">{property.name}</h1>
              <p className="text-[#A3A3A3] mt-2">{property.address}, {property.zone}</p>
              
              {/* View Counter */}
              <PropertyViewCounter propertyId={property.id} className="mt-3" />
              
              <div className="mt-6 pt-6 border-t border-white/5">
                <p className="text-3xl text-[#D4AF37] font-mono">{formatPrice(property.price_usd)}</p>
              </div>
              
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-[#A3A3A3]">Área:</span> <span className="text-white">{formatArea(property.area_m2)}</span></div>
                {property.bedrooms > 0 && <div><span className="text-[#A3A3A3]">Habitaciones:</span> <span className="text-white">{property.bedrooms}</span></div>}
                {property.bathrooms > 0 && <div><span className="text-[#A3A3A3]">Baños:</span> <span className="text-white">{property.bathrooms}</span></div>}
                {property.parking_spaces > 0 && <div><span className="text-[#A3A3A3]">Parqueos:</span> <span className="text-white">{property.parking_spaces}</span></div>}
              </div>
              
              <div className="mt-6 pt-6 border-t border-white/5 space-y-3">
                <button 
                  onClick={handleWhatsAppClick}
                  className="flex items-center justify-center gap-2 w-full py-3 bg-[#25D366] hover:bg-[#20BD5A] text-white font-medium rounded-sm transition-colors"
                  data-testid="whatsapp-btn"
                >
                  <MessageCircle size={20} />
                  WhatsApp
                </button>
                <button 
                  onClick={() => setShowContactModal(true)}
                  className="flex items-center justify-center gap-2 w-full py-3 bg-[#D4AF37] hover:bg-[#FFE082] text-black font-medium rounded-sm transition-colors"
                  data-testid="contact-btn"
                >
                  <User size={20} />
                  Ser Contactado
                </button>
                
                {/* Tour Request Button */}
                <TourRequestButton property={property} />
                
                {/* Price Alert Button */}
                <PriceAlertButton property={property} />
              </div>
              
              {/* Social Share Buttons */}
              <ShareButtons property={property} className="mt-6 pt-6 border-t border-white/5" />
            </div>
          </div>
        </div>
      </section>
      
      {/* Similar Properties Section */}
      {property && (
        <section className="py-12 bg-[#050505]">
          <div className="container-custom px-4 sm:px-6">
            <SimilarProperties property={property} limit={3} />
          </div>
        </section>
      )}
      
      {/* Related Articles Section */}
      {property && (
        <section className="py-12 bg-[#0a0a0a]">
          <div className="container-custom px-4 sm:px-6">
            <RelatedArticles property={property} limit={3} />
          </div>
        </section>
      )}
      
      <Footer />

      {/* Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">¿Te interesa esta propiedad?</DialogTitle>
            <p className="text-[#A3A3A3] text-sm mt-1">
              Déjanos tus datos y un asesor te contactará pronto.
            </p>
          </DialogHeader>
          
          <form onSubmit={handleContactSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-white">Nombre Completo *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={18} />
                <Input
                  value={contactForm.name}
                  onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                  className="pl-10 bg-[#1a1a1a] border-white/10 text-white"
                  placeholder="Tu nombre completo"
                  required
                  data-testid="contact-name-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Número de Celular *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={18} />
                <Input
                  value={contactForm.phone}
                  onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="pl-10 bg-[#1a1a1a] border-white/10 text-white"
                  placeholder="+502 1234-5678"
                  required
                  data-testid="contact-phone-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Correo Electrónico</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={18} />
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                  className="pl-10 bg-[#1a1a1a] border-white/10 text-white"
                  placeholder="tu@email.com"
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            
            <div className="bg-[#1a1a1a] p-3 rounded-sm">
              <p className="text-[#A3A3A3] text-sm">
                <span className="text-[#D4AF37]">Propiedad:</span> {property.name}
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowContactModal(false)}
                className="flex-1 border-white/10 text-white hover:bg-white/5"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[#D4AF37] text-black hover:bg-[#FFE082]"
                data-testid="submit-contact-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  "Enviar"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyPage;
